import angular from 'angular';
import {
    ILocationService,
    IPromise,
    IRootScopeService,
    ITimeoutService,
} from 'angular';
import { hasOwnProperty, isBlacklistedFilter, isFilterKey } from '../functions';

export function mapFilterValueToQSValue(value: unknown): string | unknown {
    switch (value) {
        case null:
            return 'variable';
        default:
            return value;
    }
}

export function mapQSValueToFilterValue(
    value: string
): string | number | boolean | null {
    const val = parseInt(value);
    if (val.toString() === value) {
        return val;
    }
    switch (value) {
        case 'variable':
            return null;
        case 'true':
            return true;
        case 'false':
            return false;
        default:
            return value;
    }
}

export function mapFilterValuesToQSValue(filter: unknown[]): string {
    const urlValues = filter.map(mapFilterValueToQSValue);
    const sorted = urlValues.sort();
    const value = sorted.join(',');
    return value;
}

export class FilterServicePersisted {
    filters: Record<string, unknown[]> = {};
    knownFullLengths = Object.create(null);
    actions: (() => void)[] = [];
    timeout?: IPromise<void>;

    static $inject = ['$rootScope', '$location', '$timeout'];
    constructor(
        protected $rootScope: IRootScopeService,
        protected $location: ILocationService,
        protected $timeout: ITimeoutService,
        protected persist = true
    ) {}

    enqueueAction(action: () => void): void {
        this.actions.push(action);
        if (!this.timeout) {
            this.timeout = this.$timeout(() => {
                do {
                    const evalme = this.actions.shift();
                    evalme && evalme();
                } while (this.actions.length > 0);
                this.timeout = undefined;
            });
        }
    }

    clearSearchFilters(): void {
        if (!this.persist) {
            return;
        }
        this.enqueueAction(() => {
            Object.keys(this.$location.search())
                .filter(isFilterKey)
                .forEach((key) => this.$location.search(key, null).replace());
        });
    }

    pushIntoSearch(): void {
        if (!this.persist) {
            return;
        }

        const qs = Object.keys(this.filters).reduce<[string, string][]>(
            (acc, name) => {
                if (isBlacklistedFilter(name)) {
                    return acc;
                }
                const filter = this.filters[name];
                const maxLength = this.knownFullLengths[name];
                if (
                    !filter ||
                    filter.length === 0 ||
                    filter.length === maxLength
                ) {
                    return acc;
                }
                const value = mapFilterValuesToQSValue(filter);
                const key = `filter_${name}`;
                acc.push([key, value]);
                return acc;
            },
            []
        );

        qs.forEach(([key, value]) => {
            this.enqueueAction(() => {
                this.$location.search(key, value);
            });
        });
    }

    toggle(filter: string, object: string | number | { id: number | string; }, maxLength = -1, updateQueryString = true): void {
        if (updateQueryString) {
            this.clearSearchFilters();
        }

        // fallback for scalars...
        const value =  angular.isString(object) || angular.isNumber(object) ? object: object.id;

        //If nothing has been set for this filter then it needs to be initialized
        if (!this.filters[filter]) {
            this.filters[filter] = [];
        }
        const index = this.filters[filter].indexOf(value);
        if (index === -1) {
            this.filters[filter].push(value);
        } else {
            this.filters[filter].splice(index, 1);
            // Remove filter if it's empty, so as to trigger the watches
            if (this.filters[filter].length === 0) {
                delete this.filters[filter];
            }
        }
        if (maxLength > -1) {
            this.knownFullLengths[filter] = maxLength;
        }
        if (updateQueryString) {
            this.pushIntoSearch();
        }
    }

    set(filter: string, value: Array<string | number>, maxLength = -1, updateQueryString = true): void {
        if (updateQueryString) {
            this.clearSearchFilters();
        }
        if (value.length) {
            this.filters[filter] = value;
        } else {
            this.reset(filter);
        }
        if (maxLength > -1) {
            this.knownFullLengths[filter] = maxLength;
        }
        if (updateQueryString) {
            this.pushIntoSearch();
        }
    }

    setFilterMaxLength(filter: string, maxLength: number): void {
        if (maxLength && maxLength > 0) {
            this.knownFullLengths[filter] = maxLength;
        }
    }

    reset(filter: string): void {
        // console.log('reset', { filters: this.filters, filter, notify });
        if (hasOwnProperty(this.filters, filter)) {
            delete this.filters[filter];

            if (!isBlacklistedFilter(filter)) {
                this.clearSearchFilters();
                this.pushIntoSearch();
            }
        }
    }

    resetAll(): void {
        this.clearSearchFilters();
        angular.forEach(
            this.filters,
            (filter, key) => delete this.filters[key]
        );
        this.pushIntoSearch();
    }

    get() {
        return this.filters;
    }

    hasFilter(name: string, value?: string | number): boolean {
        const values = this.filters[name];
        if (!values) {
            return false;
        }
        const index = values.indexOf(value);
        const has = index !== -1;
        return has;
    }

    restoreFromQueryString(): void {
        if (!this.persist || !this.hasQueryString()) {
            return;
        }
        const currentSearch = this.$location.search();
        Object.keys(currentSearch).forEach((param) => {
            if (isFilterKey(param) && currentSearch[param]) {
                const name = param.replace('filter_', '');
                const value = currentSearch[param]
                    .split(',')
                    .map(mapQSValueToFilterValue);
                this.set(name, value, -1, false);
            }
        });
    }

    hasQueryString(): boolean {
        return (
            Object.keys(this.$location.search()).filter(isFilterKey).length > 0
        );
    }

    /**
     * @deprecated use cleanFilter instead and call on each field, we shouldn't be passing
     * the entire vm into this function.
     * @param vm
     * @param filterMappings
     */
    validateFilters(
        vm: Record<string, FilterOption[]>,
        filterMappings: Record<string, string>
    ): void {
        Object.keys(filterMappings).forEach((name) => {
            const mappedKey = filterMappings[name];
            if (!mappedKey) {
                // console.warn(`No mapping for '${k}' found!`);
                return;
            }
            const options = vm[mappedKey];
            if (options) {
                // console.warn(`Mapping '${mappedKey}' for '${k}' empty!`);
                return;
            }
            this.cleanFilter(name, options);
        });
    }

    // ensure only allowed values are in the filter;
    cleanFilter(name: string, options: FilterOption[]) {
        const values = this.filters[name];
        if (!values) {
            // nothing to validate.
            return;
        }
        const optionIds = options.map((x) => x.id);
        const cleanedValues = values.filter((i) => {
            const value = i === '' ? null : i;
            const index = optionIds.indexOf(value);
            const valid = index !== -1;
            if (!valid) {
                console.log('FilterServicePersisted.cleanFilter, not valid', {name, i, value, index, valid});
            }
            return valid;
        });
        if (cleanedValues.length !== values.length)
            // update if values were removed
            this.set(name, cleanedValues);
    }
}

export class FilterService2 extends FilterServicePersisted {
    static $inject = ['$rootScope', '$location', '$timeout'];
    constructor(
        protected $rootScope: IRootScopeService,
        protected $location: ILocationService,
        protected $timeout: ITimeoutService
    ) {
        super($rootScope, $location, $timeout, false);
    }
}
